import React from 'react';
import { connect, ConnectedProps } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { RouteComponentProps } from 'react-router-dom';

import GridShowMoreButton from '@common/components/grid-show-more';

import { useThunkDispatch } from '../../../../common/hooks';
import { pageWrapper, EEventNames } from '../../../../../client/analytics';
import { SubNavigationBar, TopNavigationBar } from '@common/components/navigation-bar';
import Container from '../../../../common/components/container';
import AsyncList from '../../../../common/components/list/async';
import { Button } from '../../../../common/components/button';
import Permission from '../../../../common/components/permission';
import { EventItemEmployeeComponent } from '../../components/event-item';
import * as organisationSelector from '../../../organisation/selectors/organisation';
import * as userSelector from '../../../core/selectors/logged-user';
import * as eventActions from '../../actions';
import * as eventsSelector from '../../selector';
import { EPermissions } from '../../../../common/definitions';
import { EEventStatus } from '../../definitions';
import type { EventForUserDto } from '../../types';
import type { StoreState } from '../../../../common/types/store';
import { EventsPlaceholder } from '../../components/events-placeholder';

const mapStateToProps = (state: StoreState) => ({
  loggedUser: userSelector.selected(state),
  organisation: organisationSelector.selected(state),
  events: eventsSelector.getUserScopeEvents(state),
  counts: state.events.counts,
});

const connector = connect(mapStateToProps);

type Props = RouteComponentProps<{ networkId?: string, filter?: 'past' }> & ConnectedProps<typeof connector>;

export const UnwrapperEventsContainer = ({
  history,
  match,
  events,
  counts,
}: Props) => {
  const { t } = useTranslation();
  const thunkDispatch = useThunkDispatch();

  const statusLookup = {
    all: {
      filter: undefined,
      count: counts.upcoming,
    },
    past: {
      filter: EEventStatus.PAST,
      count: counts.past,
    },
  };

  // Map url to status
  const currentStatus = statusLookup[match.params.filter || 'all'];

  return (
    <>
      <SubNavigationBar title={t('core:tab_events')}>
        <SubNavigationBar.Item
          exact
          icon="event"
          path={`/networks/${match.params.networkId}/events`}
          title={t('events:filter_upcoming')}
          count={counts.upcoming}
        />
        <SubNavigationBar.Item
          icon="check_circle"
          path={`/networks/${match.params.networkId}/events/filter/past`}
          title={t('events:filter_past')}
          count={counts.past}
        />
      </SubNavigationBar>
      <Container name="Events">
        <TopNavigationBar
          title={t('events:filter', { context: match.params.filter || 'upcoming' })}
          count={currentStatus.count}
          action={(
            <Permission name={EPermissions.ORGANISATION_EVENTS_CREATE}>
              <Button type="primary" iconRight="add" onClick={() => history.push('/admin/events/create')}>
                {t('events:create_event')}
              </Button>
            </Permission>
          )}
        />
        <Container.Content>
          <AsyncList
            containerClassName="EventsList"
            // @ts-expect-error
            items={events}
            data={{
              useCursor: true,
              dispatch: thunkDispatch,
              // @ts-expect-error
              onFetch: eventActions.fetchMyEvents,
              filter: {
                networkId: match.params.networkId,
                filter: currentStatus.filter || EEventStatus.UPCOMING,
              },
            }}
            renderRow={EventItemEmployeeComponent}
            // @ts-expect-error
            rowProps={(item: EventForUserDto) => ({
              networkId: match.params.networkId,
              onClick: () => history.push(`/networks/${match.params.networkId}/events/${item.id}`),
              onEdit: () => history.push(`/admin/events/edit/${item.id}`),
            })}
            placeholder={<EventsPlaceholder />}
            ShowMoreComponent={({ onShowMore, isFetching }) => (
              <GridShowMoreButton onShowMore={onShowMore} isFetching={isFetching} />
            )}
          />
        </Container.Content>
      </Container>
    </>
  );
};

export const EventsContainer = pageWrapper(EEventNames.VISITED_SURVEYS_PAGE)(connector(UnwrapperEventsContainer));
